/**
 * Save a Blob to disk with a "Save as…" picker when the browser supports
 * it, with explicit fallbacks for iframe / pop-up-blocked contexts.
 *
 * Why three tiers?
 *   1. ``showSaveFilePicker`` — best UX (real native Save As dialog).
 *      Only available top-frame Chromium. Blocked in iframes.
 *   2. Programmatic ``<a download>`` click — top-frame Safari / Firefox
 *      fallback. Drops into Downloads folder silently.
 *   3. **User-initiated click** — when in an iframe, neither (1) nor (2)
 *      work (cross-origin sandbox blocks both). Instead of trying and
 *      failing silently, we return a ``manual_action`` outcome carrying
 *      the blob URL + suggested filename. The CALLER (e.g. ResultsPage)
 *      renders a sonner toast with a button the user clicks. THAT click
 *      is a fresh user gesture in the iframe context, which IS allowed
 *      to open a new tab. The new tab renders the PDF; the user saves
 *      with Ctrl/Cmd+S.
 *
 * Returns:
 *   { saved: true,  location: 'picked' | 'downloads', filename }
 *   { saved: false, cancelled: true }
 *   { saved: false, manual_action: true, url, filename, mimeType }
 *
 * Never throws on user cancellation. Re-throws on real I/O failures so
 * the caller can render an error toast.
 */

const KNOWN_TYPES = {
  // Maps a content-type → File System Access API ``types`` payload.
  // Each value lists at least the canonical extension. Browsers use
  // this to pre-filter the file-picker AND to suggest the extension
  // when the user types a name with no suffix.
  'application/xml':            { description: 'IDS Specification',  accept: { 'application/xml': ['.ids', '.xml'] } },
  'application/zip':            { description: 'BCF Archive',        accept: { 'application/zip': ['.bcfzip', '.zip'] } },
  'application/pdf':            { description: 'PDF Document',       accept: { 'application/pdf': ['.pdf'] } },
  'text/csv':                   { description: 'CSV Spreadsheet',    accept: { 'text/csv': ['.csv'] } },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    description: 'Excel Spreadsheet', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
  },
};

/**
 * Detect whether the current document is loaded in an iframe.
 * Try/catch handles cross-origin parents where direct comparison
 * throws — in that case we ARE in an iframe (we just can't see out).
 */
function _isInsideIframe() {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    return true;
  }
}

function _legacyDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Slightly delay the revoke so the browser has time to actually
  // start the download before we yank the URL out from under it.
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
  return { saved: true, location: 'downloads', filename };
}

/**
 * @param {Blob} blob
 * @param {string} filename — suggested name (with extension)
 * @param {string} [mimeType] — optional content-type hint
 * @returns {Promise<{saved: boolean, location?: string, filename?: string,
 *                    cancelled?: boolean, manual_action?: boolean,
 *                    url?: string, mimeType?: string}>}
 */
export async function saveBlobWithPicker(blob, filename, mimeType) {
  const inIframe = _isInsideIframe();

  // ── Tier 1 — native "Save as…" dialog ────────────────────────────────────
  // Only attempted top-frame. Inside an iframe the cross-origin sandbox
  // throws SecurityError before the dialog even renders, so we don't
  // bother trying.
  if (
    !inIframe
    && typeof window !== 'undefined'
    && typeof window.showSaveFilePicker === 'function'
  ) {
    try {
      const types = mimeType && KNOWN_TYPES[mimeType]
        ? [KNOWN_TYPES[mimeType]]
        : undefined;
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        ...(types ? { types } : {}),
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { saved: true, location: 'picked', filename: handle.name || filename };
    } catch (e) {
      if (e && (e.name === 'AbortError' || e.code === 20)) {
        return { saved: false, cancelled: true };
      }
      // SecurityError / NotAllowedError — fall through to a lower tier
      // rather than claim success on a write that never happened.
      // eslint-disable-next-line no-console
      console.warn('showSaveFilePicker failed, falling back:', e);
    }
  }

  // ── Tier 3 — iframe-safe manual action ───────────────────────────────────
  // We return a ``manual_action`` outcome carrying the blob itself + a
  // blob URL fallback. The caller renders a toast with a button. The
  // user click is a fresh user gesture that the iframe sandbox lets us
  // use to ``window.open`` a top-level helper page. That helper page
  // (``/save.html``) then receives the blob via ``postMessage`` and
  // calls ``showSaveFilePicker`` itself — from its TOP-LEVEL context
  // where it actually works. End result: a real native Save As dialog
  // inside the Emergent preview iframe.
  //
  // The blob URL is kept as a final-final fallback for the case where
  // the helper-tab open is blocked (pop-up blocker, browser policy).
  if (inIframe) {
    const url = URL.createObjectURL(blob);
    return {
      saved: false,
      manual_action: true,
      blob,                                                          // for postMessage transfer to the helper tab
      url,                                                           // pop-up-blocked preview fallback
      filename,
      mimeType: mimeType || blob.type || 'application/octet-stream',
    };
  }

  // ── Tier 2 — legacy anchor download ──────────────────────────────────────
  // Top-frame Safari / Firefox — silent drop into Downloads folder.
  return _legacyDownload(blob, filename);
}

/**
 * Helper used by the manual-action toast button. Opens the AHA
 * save-handoff helper page (``/save.html``) in a new top-level tab
 * and ``postMessage``s the blob to it. The helper page calls
 * ``showSaveFilePicker`` from its TOP-LEVEL context, where the
 * iframe sandbox no longer applies — so the user gets a real native
 * "Save As…" dialog and can pick any folder on their computer.
 *
 * Why a separate HTML page instead of opening the API URL directly?
 *   The download endpoint requires an Authorization: Bearer header
 *   that ``window.open(url)`` cannot attach. By passing the already-
 *   fetched blob via postMessage we avoid having to leak the JWT in
 *   the URL or invent a one-shot signed-URL endpoint.
 *
 * Why a separate HTML page instead of a React route?
 *   It's a static file (zero JS bundle to load) so the new tab is
 *   responsive immediately. The dialog races a slow CRA bundle and
 *   loses every time.
 *
 * MUST be called from a real user gesture (button click). Otherwise
 * the browser blocks ``window.open``. This is enforced by Tier 3 of
 * ``saveBlobWithPicker`` always returning ``manual_action`` (no
 * automatic open) and the caller wiring this function to a toast
 * action button.
 *
 * Returns ``true`` when the helper tab opened successfully. The
 * actual save completion / cancel / error is communicated back via
 * the ``onProgress`` callback (optional). Pop-up-blocked browsers
 * fall back to opening the blob URL for in-tab PDF preview (then
 * the user can Ctrl/Cmd+S as a last resort).
 */
export function openManualDownload(outcome, onProgress) {
  if (!outcome) return false;
  // Backward compatibility for callers that retained only the blob URL.
  // The helper-page handoff requires an actual Blob; without one, opening the
  // URL directly is the only functional path and avoids posting undefined data.
  if (!outcome.blob) {
    if (!outcome.url) return false;
    const fallback = window.open(outcome.url, '_blank', 'noopener,noreferrer');
    if (fallback) {
      setTimeout(() => URL.revokeObjectURL(outcome.url), 120_000);
    }
    return Boolean(fallback);
  }
  const origin = window.location.origin;
  const helperUrl = `${origin}/save.html`;
  const newTab = window.open(helperUrl, '_blank');
  if (!newTab) {
    // Pop-up blocked. Final-final fallback: open the blob URL so the
    // user at least sees the PDF and can save with Ctrl/Cmd+S.
    if (outcome.url) {
      const fallback = window.open(outcome.url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(outcome.url), 120_000);
      if (typeof onProgress === 'function') {
        onProgress({ type: 'popup-blocked' });
      }
      return Boolean(fallback);
    }
    return false;
  }

  // Wire up a one-shot message bus for THIS save tab. We can't simply
  // hold the newTab reference and post — Chrome's iframe security
  // requires the receiving page to initiate the conversation
  // (postMessage from the iframe parent without the helper page first
  // signalling "ready" is rejected because the new tab hasn't loaded
  // yet). So we wait for the ``aha-save-ready`` message and respond.
  const handleMessage = (evt) => {
    if (evt.origin !== origin) return;
    if (evt.source !== newTab) return;       // ignore other tabs / pages
    const data = evt.data || {};
    if (data.type === 'aha-save-ready') {
      // Hand the blob over. Structured-clone copies it across the
      // tab boundary so the helper page owns its own reference.
      newTab.postMessage(
        {
          type: 'aha-save-blob',
          blob: outcome.blob,
          filename: outcome.filename,
          mimeType: outcome.mimeType,
        },
        origin,
      );
    } else if (
      data.type === 'aha-save-complete' ||
      data.type === 'aha-save-error' ||
      data.type === 'aha-save-cancelled'
    ) {
      // Terminal — clean up our listener and the in-memory blob URL.
      window.removeEventListener('message', handleMessage);
      if (outcome.url) {
        URL.revokeObjectURL(outcome.url);
      }
      if (typeof onProgress === 'function') {
        onProgress(data);
      }
    }
  };
  window.addEventListener('message', handleMessage);

  // Safety: if the user closes the helper tab without saving, our
  // listener would leak forever. Clear it after 5 minutes regardless.
  setTimeout(() => window.removeEventListener('message', handleMessage), 5 * 60 * 1000);

  return true;
}

export const FILE_SYSTEM_ACCESS_SUPPORTED =
  typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function';

export const IS_INSIDE_IFRAME = _isInsideIframe();
